/**
 * RecentActivity Widget (T054)
 * Recent customer interactions and events
 */

import type { DashboardData } from '../../hooks/useDashboard';

interface Props {
  activity: DashboardData['recentActivity'];
}

function timeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHr = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMin < 1) {
    return 'Just now';
  }
  if (diffMin < 60) {
    return `${diffMin}m ago`;
  }
  if (diffHr < 24) {
    return `${diffHr}h ago`;
  }
  if (diffDays < 7) {
    return `${diffDays}d ago`;
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function RecentActivity({ activity }: Props) {
  if (!activity || activity.length === 0) {
    return (
      <div className="claude-card overflow-hidden">
        <div className="px-6 py-5 border-b border-border">
          <h2 className="text-xl font-semibold tracking-tight">Recent Activity</h2>
        </div>
        <div className="claude-empty-state">
          <p className="claude-empty-state-title">No recent activity</p>
          <p className="claude-empty-state-description">
            Activity will appear here as events occur.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="claude-card overflow-hidden">
      <div className="px-6 py-5 border-b border-border">
        <h2 className="text-xl font-semibold tracking-tight">Recent Activity</h2>
        <p className="text-sm text-muted-foreground mt-1">Latest events across your business</p>
      </div>
      <div className="divide-y divide-border max-h-[400px] overflow-y-auto">
        {activity.map((item) => (
          <div
            key={`${item.type}-${item.id}`}
            className="px-6 py-3 hover:bg-muted/30 transition-colors"
          >
            <div className="flex items-start gap-3">
              <span className="text-lg flex-shrink-0 mt-0.5">{item.icon}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">{item.action}</p>
                <p className="text-sm text-muted-foreground truncate">{item.description}</p>
              </div>
              <span className="text-xs text-muted-foreground whitespace-nowrap flex-shrink-0">
                {timeAgo(item.timestamp)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
